/**
 * Send a file to a storage host, with progress, a timeout, and cancellation.
 *
 * `XMLHttpRequest` rather than `fetch`, for one reason that has no workaround:
 * **fetch cannot report upload progress.** It resolves when the response
 * arrives, and everything before that is a black box. `xhr.upload.onprogress`
 * is the only way a browser will tell you how many bytes have actually left the
 * machine, and on a survey phone pushing a five-minute video over rural cell
 * the difference between a progress bar and a spinner is the difference between
 * waiting and force-quitting.
 *
 * It also solves two things `fetch` leaves open:
 *
 * - **A timeout.** `fetch` has none by default. A stalled connection hangs
 *   until the OS gives up, which can be minutes, and the app has nothing to say
 *   in the meantime.
 * - **Distinguishing failure modes.** `onerror`, `ontimeout` and `onabort` are
 *   separate handlers, so "the network died", "it stalled" and "you canceled"
 *   do not all arrive as one opaque `TypeError: Failed to fetch`.
 *
 * This is the second place the web app talks to a host that is not our API —
 * see `DocumentService` on why a bearer token must never be sent to storage.
 * Nothing here reads a JSON body, so nothing is laundered past a schema.
 */

import { isRetriableStatus } from '@/helpers/retriableStatus'

export interface UploadProgress {
  loaded: number
  total: number
  /** 0-100, or null when the total length is not known. */
  percent: number | null
}

export interface UploadRequest {
  url: string
  method: 'PUT' | 'POST'
  body: XMLHttpRequestBodyInit
  headers?: Record<string, string>
  /** Milliseconds of no activity before giving up. */
  timeoutMs: number
  signal?: AbortSignal
  onProgress?: (progress: UploadProgress) => void
}

/** Why an upload failed, so a caller can decide whether retrying is sensible. */
export type UploadFailureKind = 'network' | 'timeout' | 'aborted' | 'status'

export class UploadError extends Error {
  constructor(
    message: string,
    readonly kind: UploadFailureKind,
    /** The HTTP status, when there was one. Zero otherwise. */
    readonly status: number = 0,
  ) {
    super(message)
    this.name = 'UploadError'
  }

  /**
   * Whether trying again could plausibly work.
   *
   * A dropped connection or a stall might; a 4xx will not, because the host has
   * looked at the request and refused it. 408 and 429 are the exceptions — both
   * explicitly mean "not now" rather than "not ever". Cancellation is never
   * retried, because the person asked for it to stop.
   */
  get isRetriable(): boolean {
    if (this.kind === 'aborted') return false
    if (this.kind !== 'status') return true
    return isRetriableStatus(this.status)
  }
}

export function uploadWithProgress(request: UploadRequest): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (request.signal?.aborted === true) {
      reject(new UploadError('Upload canceled', 'aborted'))
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open(request.method, request.url, true)
    xhr.timeout = request.timeoutMs

    for (const [name, value] of Object.entries(request.headers ?? {})) {
      xhr.setRequestHeader(name, value)
    }

    const onAbort = (): void => {
      xhr.abort()
    }
    request.signal?.addEventListener('abort', onAbort, { once: true })
    const cleanup = (): void => {
      request.signal?.removeEventListener('abort', onAbort)
    }

    if (request.onProgress !== undefined) {
      const report = request.onProgress
      xhr.upload.onprogress = (event: ProgressEvent): void => {
        report({
          loaded: event.loaded,
          total: event.total,
          // `lengthComputable` is false for a body whose size the browser
          // cannot determine up front. Reporting a percentage anyway would
          // mean dividing by zero and rendering a bar at NaN%.
          percent: event.lengthComputable
            ? Math.round((event.loaded / event.total) * 100)
            : null,
        })
      }
    }

    xhr.onload = (): void => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
        return
      }
      reject(
        new UploadError(
          `Storage returned ${String(xhr.status)}`,
          'status',
          xhr.status,
        ),
      )
    }

    xhr.onerror = (): void => {
      cleanup()
      reject(new UploadError('The connection dropped', 'network'))
    }

    xhr.ontimeout = (): void => {
      cleanup()
      reject(new UploadError('The upload stalled', 'timeout'))
    }

    xhr.onabort = (): void => {
      cleanup()
      reject(new UploadError('Upload canceled', 'aborted'))
    }

    xhr.send(request.body)
  })
}
